import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import CommonModal from '@/components/common/CommonModal';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { IAddReturnReasonForm, IReturnReason } from '@/interfaces/ReturnReason/returnReason.interface';

interface AddReturnReasonProps {
    showAddModal: boolean;
    setShowAddModal: (show: boolean) => void;
    modalMode: 'create' | 'edit';
    selectedReason?: IReturnReason | null;
    onSuccess?: () => void;
}

export const AddReturnReason: React.FC<AddReturnReasonProps> = ({
    showAddModal,
    setShowAddModal,
    modalMode,
    selectedReason,
    onSuccess,
}) => {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<IAddReturnReasonForm>();

    useEffect(() => {
        if (showAddModal) {
            if (modalMode === 'edit' && selectedReason) {
                reset({
                    reason: selectedReason.reason
                });
            } else {
                reset({
                    reason: '',
                });
            }
        }
    }, [showAddModal, modalMode, selectedReason, reset]);

    const onSubmit = async (data: IAddReturnReasonForm) => {
        try {
            const url = modalMode === 'edit' && selectedReason
                ? `${API_URL + API_ENDPOINT.returnReason}/${selectedReason.id}`
                : `${API_URL + API_ENDPOINT.returnReason}/add`; // As per user provided endpoint /YE001/api/v1/return-requests/reason/add

            const method = modalMode === 'edit' ? 'PATCH' : 'POST';

            const response = await callApi<any>(
                url,
                {
                    method: method,
                    data: data,
                },
            );

            showToastNotification('success', response?.message || `Return reason ${modalMode === 'edit' ? 'updated' : 'created'} successfully`);
            setShowAddModal(false);
            onSuccess?.();
        } catch (error) {
            ThrowError(error);
        }
    };

    return (
        <CommonModal
            open={showAddModal}
            onOpenChange={setShowAddModal}
            title={modalMode === 'edit' ? "Edit Return Reason" : "Create New Return Reason"}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                    label="Reason"
                    name="reason"
                    requiredIcon
                    placeholder="e.g. Defective Product"
                    error={errors.reason?.message}
                    {...register('reason', {
                        ...errorTypes.required('Reason'),
                    })}
                />

                <div className="flex justify-end gap-3 pt-6 border-t border-border">
                    <button
                        type="button"
                        onClick={() => setShowAddModal(false)}
                        className="h-9 px-4 rounded-lg border text-sm font-bold hover:bg-secondary transition-colors"
                    >
                        Cancel
                    </button>
                    <Button type="submit" isLoading={isSubmitting}>
                        {modalMode === 'edit' ? "Update Reason" : "Create Reason"}
                    </Button>
                </div>
            </form>
        </CommonModal>
    );
};
